import { useCallback, useContext, useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import AppGlobalsContext from "app/AppGlobalsContext";
import { Tunnel } from "../types";

const STORAGE_KEY = "pfs:searchHistory";
const MAX_HISTORY_LENGTH = 5;

type SearchOccupancy = {
	adults?: number;
	children?: number;
};

type CodeLabel = { code?: string; label?: string; type?: string };

type SearchData = {
	departureCity?: CodeLabel;
	destinationResort?: CodeLabel;
	searchedAccommodation?: { id?: string; name?: string; places?: unknown[] };
	departureDate?: number;
	endDate?: number;
	cabin?: CodeLabel;
	occupancies?: SearchOccupancy[];
	adults?: number;
	children?: number;
	infants?: number;
	stopoverCityId?: number;
	stopoverDirection?: number;
	[key: string]: unknown;
};

type SearchEntry = {
	id: string;
	data: SearchData;
};

type SearchHistoryByTunnel = Record<Tunnel, SearchEntry[]>;

const INITIAL_SEARCH_HISTORY: SearchHistoryByTunnel = {
	sdpHolidays: [],
	sdpStopover: [],
	hotelOnly: [],
};

const getPassengerCount = (search?: SearchData) =>
	(Number(search?.adults) || 0) +
	(Number(search?.infants) || 0) +
	(Number(search?.children) || 0);

/** Compares two timestamps on the UTC calendar day, so a start-of-day and an end-of-day date match. */
const isSameDay = (a?: number, b?: number) => {
	if (typeof a !== "number" || typeof b !== "number") {
		return a === b;
	}

	return new Date(a).toISOString().slice(0, 10) === new Date(b).toISOString().slice(0, 10);
};

/** Compares two searches on the fields surfaced in the history list, so visually identical entries are deduped. */
const areSearchesEqual = (a?: SearchData, b?: SearchData) =>
	a?.departureCity?.code === b?.departureCity?.code &&
	a?.destinationResort?.code === b?.destinationResort?.code &&
	a?.searchedAccommodation?.id === b?.searchedAccommodation?.id &&
	isSameDay(a?.departureDate, b?.departureDate) &&
	isSameDay(a?.endDate, b?.endDate) &&
	a?.cabin?.code === b?.cabin?.code &&
	a?.stopoverCityId === b?.stopoverCityId &&
	a?.stopoverDirection === b?.stopoverDirection &&
	getPassengerCount(a) === getPassengerCount(b) &&
	(a?.occupancies?.length || 0) === (b?.occupancies?.length || 0);

/** Keeps only upcoming searches (those whose departure date has not passed yet). */
const keepUpcomingSearches = (entries: SearchEntry[] = []) => {
	const today = new Date();
	return entries.filter(entry => {
		if (entry?.data?.departureDate) {
			return new Date(entry.data.departureDate) >= today;
		}

		return true;
	});
};

/** Removes duplicate searches, keeping the first occurrence so freshly added entries win over older ones. */
const dedupeSearches = (entries: SearchEntry[] = []) =>
	entries.reduce<SearchEntry[]>((kept, entry) => {
		const isDuplicate = kept.some(keptEntry => areSearchesEqual(keptEntry.data, entry.data));

		if (!isDuplicate) {
			kept.push(entry);
		}

		return kept;
	}, []);

const readStoredHistory = (storageKey: string) => {
	try {
		const stored = localStorage.getItem(storageKey);
		const parsed = (stored ? JSON.parse(stored) : {}) as Partial<SearchHistoryByTunnel>;
		return { ...INITIAL_SEARCH_HISTORY, ...parsed };
	} catch {
		return INITIAL_SEARCH_HISTORY;
	}
};

export const getLatestSearchEntry = (shop: string, tunnel: Tunnel) => {
	if (typeof window === "undefined") {
		return undefined;
	}

	try {
		const stored = window.localStorage?.getItem(`${STORAGE_KEY}:${shop}`);

		if (!stored) {
			return undefined;
		}

		const parsed = (JSON.parse(stored) || {}) as Partial<SearchHistoryByTunnel>;

		return keepUpcomingSearches(parsed[tunnel] || [])[0];
	} catch {
		return undefined;
	}
};

export const useSearchHistory = (tunnel: Tunnel) => {
	const [historyByTunnel, setHistoryByTunnel] = useState(INITIAL_SEARCH_HISTORY);

	const { shop } = useContext(AppGlobalsContext);
	const { track } = useAnalytics();

	const storageKey = `${STORAGE_KEY}:${shop}`;

	const persist = useCallback(
		(history: SearchHistoryByTunnel) => {
			localStorage.setItem(storageKey, JSON.stringify(history));
		},
		[storageKey]
	);

	useEffect(() => {
		const stored = localStorage?.getItem(storageKey);

		if (!stored) {
			return;
		}

		try {
			const parsed = JSON.parse(stored) as Partial<SearchHistoryByTunnel>;
			const cleanedHistory = (Object.keys(parsed) as Tunnel[]).reduce<
				Partial<SearchHistoryByTunnel>
			>((acc, tunnelKey) => {
				acc[tunnelKey] = dedupeSearches(keepUpcomingSearches(parsed[tunnelKey]));
				return acc;
			}, {});

			setHistoryByTunnel({ ...INITIAL_SEARCH_HISTORY, ...cleanedHistory });

			const isAnyHistoryAvailable = Object.values(cleanedHistory).some(entries =>
				Boolean(entries?.length)
			);

			if (isAnyHistoryAvailable) {
				track("last-searches-available");
			}
		} catch (err) {
			track("use-search-history-read-error", { error: (err as Error)?.message });
		}
	}, [storageKey]);

	const addSearch = useCallback(
		(searchData: SearchData) => {
			const newEntry: SearchEntry = { data: searchData, id: uuidv4() };
			const storedHistory = readStoredHistory(storageKey);
			const currentEntries = storedHistory[tunnel] || [];
			const updatedTunnelHistory = dedupeSearches([newEntry, ...currentEntries]).slice(
				0,
				MAX_HISTORY_LENGTH
			);

			const updatedHistory = { ...storedHistory, [tunnel]: updatedTunnelHistory };
			persist(updatedHistory);
			setHistoryByTunnel(updatedHistory);
		},
		[tunnel, storageKey, persist]
	);

	const getSearchById = useCallback(
		(id: string) => (historyByTunnel[tunnel] || []).find(entry => entry?.id === id),
		[tunnel, historyByTunnel]
	);

	const removeSearch = useCallback(
		(idToRemove: string) => {
			setHistoryByTunnel(prevHistory => {
				const updatedTunnelHistory = (prevHistory[tunnel] || []).filter(
					entry => entry.id !== idToRemove
				);
				const updatedHistory = { ...prevHistory, [tunnel]: updatedTunnelHistory };
				persist(updatedHistory);
				return updatedHistory;
			});
		},
		[tunnel, persist]
	);

	const clearHistory = useCallback(() => {
		setHistoryByTunnel(prevHistory => {
			const updatedHistory = { ...prevHistory, [tunnel]: [] };
			persist(updatedHistory);
			return updatedHistory;
		});
	}, [tunnel, persist]);

	return {
		history: historyByTunnel[tunnel] || [],
		addSearch,
		clearHistory,
		removeSearch,
		getSearchById,
	};
};
